import React from 'react';
import { Link } from 'react-router-dom';
import AssessmentPage from './AssessmentPage';

const AutismAssessment = () => (
  <AssessmentPage
    seo={{
      title: 'Autism Assessment for Children, Teens & Adults | Clinical Minds Psychology',
      description: 'Neuroaffirming autism assessments across two sessions on different days, with personal feedback and a profile written for you. Coffs Harbour, visiting clinics and telehealth.',
      canonicalUrl: 'https://www.clinicalminds.net/autism-assessment'
    }}
    heroTitle="Autism Assessment"
    heroSubtitle=""
    intro={
      <p className="body-medium">
        An autism assessment done well doesn't just answer "is it autism?". It builds a clear picture of how a person experiences the world, what they're great at, what drains them, and what kind of environments help them thrive. At Clinical Minds Psychology, assessment is neuroaffirming from start to finish. Autism is a difference in how a brain works, not a deficit to be measured, and the report you receive reflects that.
      </p>
    }
    whoFor={
      <>
      <p className="body-medium">
        Children and young people where autism is a question: perhaps school has raised concerns, perhaps things at home feel harder than they should, or perhaps your child has told you they think they might be autistic. Also families seeking a formal diagnosis to access supports such as the NDIS, or clarity after years of partial answers.
      </p>
      <p className="body-medium">
        I also assess adults, including people who have wondered for years whether autism explains how they experience the world.
      </p>
      </>
    }
    whatsInvolved={
      <p className="body-medium">
        Assessment is comprehensive, not a single questionnaire. It typically includes a detailed intake with parents or caregivers, direct assessment using recognised tools including the ADOS-2, standardised questionnaires completed by people who know the person well, and information from school where relevant. I prioritise time with the person being assessed: an intake session plus at least two assessment sessions, because good assessment can't be done in a single visit. I take time to understand strengths and interests, not just difficulties. Afterwards you receive a feedback session where results are explained in plain language, with time for every question.
      </p>
    }
    reportUsedFor={
      <p className="body-medium">
        The report provides a formal diagnostic outcome and practical, specific recommendations for home and school. It's written to support NDIS access requests, school learning adjustments, and shared understanding among the people supporting your child. It's a document written to be used, not filed away.
      </p>
    }
    feesContent={
      <>
        <p className="body-medium">
          Assessment fees depend on the scope of the assessment. See our <Link to="/fees" className="assess-link">fees page</Link> for information about funding options including the NDIS, or <Link to="/contact" className="assess-link">contact us</Link> and we'll talk it through.
        </p>
      </>
    }
    ctaText="Enquire about an autism assessment"
    enquiryParam="autism"
    extraSections={
      <>
        <section className="assess-section scroll-reveal" data-testid="assess-how-works">
          <div className="container assess-narrow">
            <h2 className="heading-1">How my autism assessments work</h2>
            <div className="assess-body">
              <p className="body-medium">
                Every part of the assessment is done by me personally, nothing is outsourced. We start with an hour together, parents for a child's assessment, or you directly for an adult assessment, before any formal testing. Then at least two assessment sessions on different days, because everyone meets the world differently from one day to the next, and seeing that matters. Afterwards you get a feedback session in plain language, practical recommendations for family, school or work, and a personal profile written for the person who was assessed, not just a report about them.
              </p>
            </div>
          </div>
        </section>
        <section className="assess-section assess-alt scroll-reveal" data-testid="assess-common-questions">
          <div className="container assess-narrow">
            <h2 className="heading-1">Common questions</h2>
            <div className="assess-body assess-qa">
              <p className="body-medium"><strong>Do you assess adults?</strong> Yes. Many of my autism assessments are for adults who have spent years wondering. It's never too late for answers that make your life make sense.</p>
              <p className="body-medium"><strong>Is the assessment neuroaffirming?</strong> Yes. I see autism as a difference in how a person experiences and processes the world, not a deficit. The assessment describes strengths as thoroughly as challenges, and difficulties are understood as mismatches between a person and their environment, not flaws in the person.</p>
              <p className="body-medium"><strong>Do you use standard diagnostic tools?</strong> Yes, my assessments use recognised tools aligned with Australian guidelines, so the outcome is recognised by schools, NDIS and health providers.</p>
            </div>
          </div>
        </section>
      </>
    }
    faqs={[
      {
        question: 'Do we need a referral?',
        answer: "You can enquire directly without a referral. Depending on your situation, a referral from a GP or paediatrician may help with rebates, and we'll let you know if that applies to you."
      },
      {
        question: 'How long does the process take?',
        answer: "It varies with the scope of the assessment, but you'll be given a clear timeline at enquiry, covering intake, assessment sessions, feedback and report delivery."
      },
      {
        question: "What if the outcome isn't autism?",
        answer: "You'll still leave with answers. The assessment builds a full picture of your child's thinking, learning and social world, so if autism isn't the best explanation, the report says what is going on and what will help."
      },
      {
        question: 'Do you assess adults?',
        answer: "Yes. We assess children, young people and adults. The process is adapted to your age and situation, and the same neuroaffirming approach applies throughout."
      }
    ]}
  />
);

export default AutismAssessment;
